import { Check, Loader2, PhoneOff } from "lucide-react";
import { cn } from "@/utils";

function formatPhoneSuccessRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  return `${Math.round(rate * 1000) / 10}%`;
}

export interface CasePhoneNumbersView {
  payer_phone_number: string;
  available_phone_numbers: string[];
  tried_phone_numbers: string[];
  all_numbers_exhausted: boolean;
  alternate_contact_number?: string | null;
  /** From intake rows: call finished with a live PA outcome on the active dial. */
  success?: string;
  fail_class?: string;
  phone_global_success_rates?: Record<string, number>;
  retry_success_likelihood?: number | null;
}

function isActiveDialSettled(view: CasePhoneNumbersView): boolean {
  if (view.success === "true") return true;
  if (view.fail_class === "live_outcome") return true;
  return false;
}

/** True when this number has finished its per-number retry cycle (exhausted or rotated past). */
export function isPhoneRetryCycleComplete(
  phone: string,
  view: CasePhoneNumbersView,
  triedSet: Set<string>,
): boolean {
  if (view.all_numbers_exhausted && phone === view.payer_phone_number) {
    return true;
  }
  // Successful calls keep payer_phone_number on the last dialed number; no tried list update.
  if (phone === view.payer_phone_number && isActiveDialSettled(view)) {
    return true;
  }
  if (triedSet.has(phone)) {
    return true;
  }
  return false;
}

export function CasePhoneNumbersTable({
  view,
  onSelectPhone,
  selectedPhone,
}: {
  view: CasePhoneNumbersView;
  onSelectPhone?: (phone: string) => void;
  selectedPhone?: string;
}) {
  const numbers =
    view.available_phone_numbers.length > 0
      ? view.available_phone_numbers
      : view.payer_phone_number
        ? [view.payer_phone_number]
        : [];

  const triedSet = new Set(view.tried_phone_numbers);
  const extraAlternate =
    view.alternate_contact_number &&
    !numbers.includes(view.alternate_contact_number)
      ? view.alternate_contact_number
      : null;

  if (numbers.length === 0 && !extraAlternate) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700">
        <PhoneOff className="h-3 w-3" />
        none
      </span>
    );
  }

  const rows = extraAlternate ? [...numbers, extraAlternate] : numbers;
  const showGlobalStats = Boolean(view.phone_global_success_rates);

  return (
    <table className="w-full min-w-[140px] border-collapse text-[11px]">
      <tbody>
        {rows.map((phone) => {
          const isActiveDial = phone === view.payer_phone_number;
          const isSelected = selectedPhone === phone;
          const isGeminiAlt =
            phone === view.alternate_contact_number &&
            !numbers.includes(phone);
          const cycleComplete = isPhoneRetryCycleComplete(phone, view, triedSet);
          const showDialSpinner = isActiveDial && !cycleComplete;
          const clickable = Boolean(onSelectPhone);
          return (
            <tr
              key={phone}
              className={cn(
                isActiveDial ? "text-foreground" : "text-muted-foreground",
                isSelected && "bg-primary/5",
              )}
            >
              <td className="w-4 pr-1 align-top pt-0.5">
                {cycleComplete ? (
                  <Check
                    className="h-3 w-3 text-green-600 dark:text-green-500"
                    aria-label="Retry cycle complete"
                  />
                ) : (
                  <span className="inline-block h-3 w-3" aria-hidden />
                )}
              </td>
              <td className="font-mono align-top">
                {clickable ? (
                  <button
                    type="button"
                    className={cn(
                      "inline-flex items-center gap-1 rounded px-0.5 text-left hover:underline",
                      isSelected && "font-medium text-primary",
                    )}
                    title="Use as dial number for retry"
                    onClick={() => onSelectPhone?.(phone)}
                  >
                    {phone}
                    {showDialSpinner ? (
                      <Loader2
                        className="h-3 w-3 shrink-0 animate-spin text-primary"
                        aria-label="Dialing"
                      />
                    ) : null}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    {phone}
                    {showDialSpinner ? (
                      <Loader2
                        className="h-3 w-3 shrink-0 animate-spin text-primary"
                        aria-label="Dialing"
                      />
                    ) : null}
                  </span>
                )}
                {isGeminiAlt ? (
                  <span
                    className="ml-1 font-sans text-[10px] text-muted-foreground"
                    title="Alternate captured by Gemini"
                  >
                    alt
                  </span>
                ) : null}
                {showGlobalStats ? (
                  <span
                    className="ml-1 font-sans text-[10px] tabular-nums text-muted-foreground"
                    title="Global success rate for this number"
                  >
                    · {formatPhoneSuccessRate(view.phone_global_success_rates?.[phone])}
                  </span>
                ) : null}
              </td>
            </tr>
          );
        })}
        {showGlobalStats && view.retry_success_likelihood != null ? (
          <tr>
            <td className="w-4 pr-1" />
            <td
              className="pt-0.5 font-sans text-[10px] tabular-nums text-muted-foreground"
              title="Likelihood of at least one success if all listed numbers are retried (1 − ∏(1 − rate))"
            >
              {formatPhoneSuccessRate(view.retry_success_likelihood)} retry success
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

/** Parse operator-edited phone list (one per line or comma-separated). */
export function parsePhoneListInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

export function formatPhoneListInput(numbers: string[]): string {
  return numbers.join("\n");
}
